import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

type InfoCardProps = {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
};

export const InfoCard = ({ icon: Icon, title, children, headerRight }: InfoCardProps) => (
  <div className="border-border flex w-full flex-col gap-4 rounded-2xl border bg-white md:h-full md:overflow-y-scroll">
    <div className="flex items-center gap-2 p-4 pb-1">
      <div className="flex flex-1 items-center gap-2">
        <Icon className="size-4 text-gray-500" />
        <p className="text-sm font-semibold text-gray-500 uppercase">{title}</p>
      </div>
      {headerRight}
    </div>
    <div className="flex flex-col gap-2">{children}</div>
  </div>
);

type InfoRowProps = {
  label: string;
  value: React.ReactNode;
  className?: string;
};

export const InfoRow = ({ label, value, className }: InfoRowProps) => (
  <div className={cn("flex items-start justify-between gap-4 px-4", className)}>
    <p className="shrink-0 text-xs font-medium text-gray-500 uppercase">{label}</p>
    <div className="text-right text-sm font-semibold break-words text-gray-900">{value ?? "-"}</div>
  </div>
);

export const InfoRowNested = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 pl-3">
    <p className="shrink-0 text-[11px] font-medium text-gray-500 uppercase">{label}</p>
    <div className="text-right text-xs font-medium break-words text-gray-900">{value ?? "-"}</div>
  </div>
);

type InfoAccordionRowProps = {
  label: string;
  value: React.ReactNode;
  children?: React.ReactNode;
};

/** Falls back to a plain InfoRow when there are no nested rows to expand. */
export const InfoAccordionRow = ({ label, value, children }: InfoAccordionRowProps) => {
  if (!children) {
    return <InfoRow label={label} value={value} />;
  }

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="content" className="border-none">
        <AccordionTrigger className="items-start gap-1.5 rounded-none bg-gray-100 px-3 py-2 hover:no-underline">
          <div className="flex w-full min-w-0 flex-row items-start justify-between gap-2">
            <p className="shrink-0 !px-1 pt-px text-left text-xs font-medium text-gray-500 uppercase">
              {label}
            </p>
            <span className="min-w-0 flex-1 text-right text-sm font-semibold break-words text-gray-900">
              {value ?? "-"}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-2 bg-gray-100 px-3 pr-5 pb-2">
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
